import React from 'react';
import { X, WarningCircle } from '@phosphor-icons/react';

// Shared style constants for error boundary UI
const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'var(--scrim)',
    zIndex: 999,
  },
  panel: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: '480px',
    maxWidth: '100vw',
    background: 'var(--surface)',
    boxShadow: 'var(--shadow-xl)',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1.5rem 2rem',
    background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent-hover) 100%)',
    color: 'var(--accent-contrast)',
  },
  headerTitle: {
    margin: 0,
    fontFamily: "var(--font-display)",
    fontSize: '1.125rem',
  },
  closeButton: {
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    color: 'var(--accent-contrast)',
    cursor: 'pointer',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    textAlign: 'center',
  },
  iconWrapper: {
    width: '64px',
    height: '64px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--danger-soft)',
    color: 'var(--danger)',
    borderRadius: '50%',
    marginBottom: '1.5rem',
  },
  errorTitle: {
    fontFamily: "var(--font-display)",
    fontSize: '1.25rem',
    color: 'var(--text-primary)',
    marginBottom: '0.5rem',
  },
  errorMessage: {
    color: 'var(--text-muted)',
    marginBottom: '1.5rem',
    maxWidth: '280px',
    lineHeight: 1.6,
  },
  buttonGroup: {
    display: 'flex',
    gap: '0.75rem',
  },
  buttonBase: {
    padding: '0.5rem 1.5rem',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  primaryButton: {
    background: 'var(--accent)',
    color: 'var(--accent-contrast)',
  },
  secondaryButton: {
    background: 'var(--surface-raised)',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
  },
};

/**
 * Error boundary specifically for the ChatPanel component.
 * Catches rendering errors in the chat panel without crashing the entire Gallery.
 */
class ChatErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ChatPanel error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleClose = () => {
    this.setState({ hasError: false, error: null });
    this.props.onClose?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <>
          <div
            className="chat-backdrop"
            onClick={this.handleClose}
            style={styles.backdrop}
          />

          <aside className="chat-panel" style={styles.panel}>
            <header style={styles.header}>
              <h2 style={styles.headerTitle}>Ask your collection</h2>
              <button
                onClick={this.handleClose}
                style={styles.closeButton}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </header>

            <div style={styles.content}>
              <div style={styles.iconWrapper}>
                <WarningCircle size={32} />
              </div>

              <h3 style={styles.errorTitle}>Something went wrong</h3>
              <p style={styles.errorMessage}>
                The chat panel encountered an error. Please try again.
              </p>

              <div style={styles.buttonGroup}>
                <button
                  onClick={this.handleRetry}
                  style={{ ...styles.buttonBase, ...styles.primaryButton }}
                >
                  Try again
                </button>
                <button
                  onClick={this.handleClose}
                  style={{ ...styles.buttonBase, ...styles.secondaryButton }}
                >
                  Close
                </button>
              </div>
            </div>
          </aside>
        </>
      );
    }

    return this.props.children;
  }
}

export default ChatErrorBoundary;
